import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import s from './SlotTrigger.module.css'

type SlotTriggerSize = 'sm' | 'md'

/**
 * A BUTTON DRAWN AS A VALUE SLOT — for the trigger that OPENS a place to type
 * rather than being one (#2406, lane b3-shell).
 *
 * ── why this exists ────────────────────────────────────────────────────────
 * The console's top-bar search is a command-palette TRIGGER: it holds no value,
 * it opens the palette, and the palette owns the typing and the focus. The
 * leading design draws it as a slot — *"search fields are slots too"*, the
 * Organization Detail sheet's `.sbox`, a `<button>` recessed like the field it
 * stands for — and the kit had no way to say that. `SearchField` is an
 * `<input>`, which would take focus and keystrokes the palette owns; `Button`
 * is a raised block, which is the material for an action ON the page. So the
 * host drew its own (`Topbar.module.css .cmd`, a `--surf-2` pill), and the
 * rogue census counted it as an app-styled control on every `/` cell.
 *
 * ── the material ───────────────────────────────────────────────────────────
 * Exactly the value slot's (`Input.module.css`): the recess at rest, one step
 * lighter on hover, the `--focus-ring` ring PLUS the brand vein along the
 * bottom cut on keyboard focus, the 2px bottom border transparent at rest so
 * lighting the vein moves nothing. The words inside read as a placeholder
 * does — `--muted` — because they say what the slot is for, not a value.
 *
 * ── the parts ──────────────────────────────────────────────────────────────
 * `icon` leads (decorative), `children` is the words, `shortcut` trails as a
 * `<kbd>` seated on the slot (decorative: the host states the shortcut in the
 * accessible name, where a reader hears it — "place of interest sign" is what
 * a screen reader says for ⌘). `iconOnly` reduces the slot to a square of the
 * control height on a fine pointer and the target floor on a coarse pointer,
 * for a row that cannot afford the words; the accessible name then carries
 * them, so the host MUST pass `aria-label`.
 */
export interface SlotTriggerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** The leading glyph. Decorative. */
  icon?: ReactNode
  /** The trailing keyboard hint, drawn as a key seated on the slot. Decorative. */
  shortcut?: ReactNode
  /** Draw the glyph alone in a square slot. The words go; `aria-label` names it. */
  iconOnly?: boolean
  /** Density role. Shells that previously used a small button keep the shared
   *  24 / 32 / 40 rhythm without repainting this trigger in the host. */
  size?: SlotTriggerSize
}

export const SlotTrigger = forwardRef<HTMLButtonElement, SlotTriggerProps>(
  function SlotTrigger({ icon, shortcut, iconOnly = false, size = 'md', className, children, ...rest }, ref) {
    return (
      <button ref={ref} type="button" {...rest}
        className={cx(s.trigger, s[size], iconOnly && s.iconOnly, className)}>
        {icon != null && <span className={s.icon} aria-hidden="true">{icon}</span>}
        {children != null && !iconOnly && <span className={s.label}>{children}</span>}
        {shortcut != null && !iconOnly && <kbd className={s.shortcut} aria-hidden="true">{shortcut}</kbd>}
      </button>
    )
  },
)
